import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import { StyledCustomButton } from './CustomButton.styles';

/**
 * CustomButton Component
 * 
 * A flexible button component that combines MUI Button with custom styling
 * using Emotion and Tailwind utilities.
 * 
 * @component
 * @example
 * ```jsx
 * <CustomButton variant="primary" size="md" onClick={handleClick}>
 *   Click Me
 * </CustomButton>
 * 
 * <CustomButton variant="outline" loading startIcon={<SaveIcon />}>
 *   Save
 * </CustomButton>
 * ```
 */
const CustomButton = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  startIcon = null,
  endIcon = null,
  onClick,
  type = 'button',
  className = '',
  ...rest
}) => {
  // Build className string for styled component
  const buttonClassName = [
    `variant-${variant}`,
    `size-${size}`,
    fullWidth && 'fullWidth',
    loading && 'loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <StyledCustomButton
      type={type}
      className={buttonClassName}
      disabled={disabled || loading}
      onClick={onClick}
      startIcon={!loading && startIcon}
      endIcon={!loading && endIcon}
      {...rest}
    >
      {loading && (
        <CircularProgress
          size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16}
          className="spinner"
          sx={{ color: 'currentColor' }}
        />
      )}
      {children}
    </StyledCustomButton>
  );
};

CustomButton.propTypes = {
  /** Button content */
  children: PropTypes.node.isRequired,
  
  /** Button style variant */
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'outline',
    'text',
    'danger',
    'success',
  ]),
  
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  
  /** Make button full width */
  fullWidth: PropTypes.bool,
  
  /** Show loading spinner */
  loading: PropTypes.bool,
  
  /** Disable button */
  disabled: PropTypes.bool,
  
  /** Icon to display before text */
  startIcon: PropTypes.node,
  
  /** Icon to display after text */
  endIcon: PropTypes.node,
  
  /** Click handler */
  onClick: PropTypes.func,
  
  /** Button type attribute */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default CustomButton;
